// A token's stat-block speeds on the DM's screen (qa-runs#1161): a token linked to an entry before speeds were stamped
// on a link holds none, so its area reads the speeds its stat block says, asked once (the DM's stat block, its copy from
// when it was matched) and never stored. A player's screen asks nothing: the stat block is the DM's alone.
import { blockSpeeds, type BlockSpeeds, type EntryRef, type MovementRules } from '@dragons-table/vtt-shared';
import { statBlock } from '../entries';

/** A token as this reads it: its entry, its sheet, and the speeds it holds. */
export interface BlockThing { id: string; entry?: Pick<EntryRef, 'segment' | 'slug' | 'id'> | undefined; character?: string | null | undefined; values?: { speed?: number | undefined; sheet?: ReadonlyArray<{ purpose: string }> | undefined } | undefined }

const known = new Map<string, BlockSpeeds | null>();
const asking = new Set<string>();

/**
 * The speeds the stat block of `o` says, or null while asked, for a token with no entry, a sheet's, or a speed of its
 * own. `ready` runs once the answer is in, so the area is drawn again.
 */
/** True while the stat block of `o` is being read: no area and no "no speed" note yet, so the note does not flash. */
export function blockSpeedsAsking(o: BlockThing, at: { campaignId: string; scene: string | null }): boolean {
  return o.entry ? asking.has(`${at.campaignId} ${at.scene ?? ''} ${o.id} ${o.entry.segment} ${o.entry.slug}`) : false;
}

export function blockSpeedsOf(o: BlockThing, at: { campaignId: string; scene: string | null; isDm: boolean; movement: MovementRules | undefined }, ready: () => void): BlockSpeeds | null {
  if (!at.isDm || !at.movement || !o.entry || o.character || o.values?.speed !== undefined) return null;
  const key = `${at.campaignId} ${at.scene ?? ''} ${o.id} ${o.entry.segment} ${o.entry.slug}`;
  if (known.has(key)) return known.get(key)!;
  if (asking.has(key)) return null;
  asking.add(key);
  const movement = at.movement;
  void statBlock(at.campaignId, o.entry, undefined, { object: o.id, scene: at.scene }).then((b) => {
    asking.delete(key);
    known.set(key, b && b !== 'gone' ? blockSpeeds(b.stats, undefined, movement) : null);
    ready();
  }, () => { asking.delete(key); });
  return null;
}
